import { memo, useState, useEffect, type KeyboardEvent, type ClipboardEvent, type DragEvent } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { first, isNil } from 'lodash';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import UI Components
import { Dropdown, ExtendedInput, Popover } from '@geneui/components';
// Import Constants
import { l, ScheduleModalViews, Hours, Days, WeekDaysKeyList, Months } from 'constants/common';
import { POSITIVE_INTEGER_REGEXP } from 'constants/regexp';
// Import UI Components
import DateSelector from 'components/common/Scheduler/DateSelector';
import RecurrenceList from 'components/common/RecurrenceList';
// Import Services
import { Helpers } from 'services';
import { UtilsHttpService } from 'services/http';
import { isSchedulerUnitPairsInvalid, resultScheduleDataCreator } from 'services/schedular';

const { customMoment, customMomentWithoutTimezoneConversion } = Helpers;
const { dailyView, weeklyView, monthlyView, annuallyView } = ScheduleModalViews;

const NON_INTEGER_KEYS = ['.', ',', 'e', 'E', '+', '-'];

// Whole number from 1 to max; '' is allowed so the field can be cleared.
const isValidRecurrenceInterval = (value: string, max?: number): boolean =>
    value === '' || (POSITIVE_INTEGER_REGEXP.test(value) && (isNil(max) || +value <= max));

const handleIntegerKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (NON_INTEGER_KEYS.includes(e.key)) e.preventDefault();
};

const handleIntegerPaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    if (!POSITIVE_INTEGER_REGEXP.test(e.clipboardData.getData('text'))) e.preventDefault();
};

const handleIntegerDrop = (e: DragEvent<HTMLInputElement>): void => {
    if (!POSITIVE_INTEGER_REGEXP.test(e.dataTransfer.getData('text'))) e.preventDefault();
};

const ScheduleCEView = ({
    modalView,
    handleViewChange,
    setDictData,
    dictData,
    timeOptions,
    hideDropdown,
    recurrenceData,
    schedulerValidationErrorText,
    isModalView,
}) => {
    const { key, isVisibleDay, isVisibleWeek, isVisibleMonth, isVisibleYear, isVisibleHour } = modalView;
    const { startDate, startTime, timeZone, endDate } = timeOptions;
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const [hour, setHour] = useState('');
    const [day, setDay] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    const [hours, setHours] = useState([]);
    const [days, setDays] = useState([]);
    const [weekDays, setWeekDays] = useState([]);
    const [months, setMonths] = useState([]);
    const [years, setYears] = useState([]);

    const [recurrenceDataLocal, setRecurrenceDataLocal] = useState(recurrenceData);

    const { getScheduleRunDates } = useCancelableRequests(
        { getScheduleRunDates: UtilsHttpService.getScheduleRunDates },
        { takeLatest: ['getScheduleRunDates'] },
    );

    const options = [
        { label: t(l.Daily), value: dailyView },
        { label: t(l.Weekly), value: weeklyView },
        { label: t(l.Monthly), value: monthlyView },
        { label: t(l.Annually), value: annuallyView },
    ];

    const updateSchedulerInfo = (selectionsObj) => {
        if (!selectionsObj || isSchedulerUnitPairsInvalid(selectionsObj)) {
            return setRecurrenceDataLocal({
                Description: t(l.InvalidUnitPairCombination),
            });
        }

        const Schedule = resultScheduleDataCreator(
            startTime,
            startDate,
            endDate,
            key,
            { [key]: selectionsObj },
            timeZone,
        );

        getRecCount(Schedule.CronSchedule, Schedule.StartDate, Schedule.EndDate, Schedule.TimeZone);
    };

    const onClickHandler = (pairValues) => {
        const initialObj = {
            hours: hours,
            days: days,
            weeks: weekDays,
            months: months,
            years: years,
            hour: hour,
            day: day,
            month: month,
            year: year,
            viewType: key,
            ...pairValues,
        };

        setDictData(initialObj, key);

        if (isModalView) {
            updateSchedulerInfo(initialObj);
        }
    };

    const addHour = (e) => {
        const { value } = e.target;
        if (!isValidRecurrenceInterval(value, 23)) return;

        setHour(value);
        onClickHandler({ hour: value, hours: [] });
    };

    const addDay = (e) => {
        const { value } = e.target;
        if (!isValidRecurrenceInterval(value, 31)) return;

        setDay(value);
        onClickHandler({ day: value, days: [] });
    };

    const addMonth = (e) => {
        const { value } = e.target;
        if (!isValidRecurrenceInterval(value, 12)) return;

        setMonth(value);
        onClickHandler({ month: value, months: [] });
    };

    const addYear = (e) => {
        const { value } = e.target;
        if (!isValidRecurrenceInterval(value)) return;

        setYear(value);
        onClickHandler({ year: value, years: [] });
    };

    const dataListToggleHandler = (data, value) => {
        if (data.includes(value)) {
            data = data.filter((item) => item !== value);
        } else {
            data.push(value);
        }

        return data;
    };

    const addHours = (index) => {
        const data = dataListToggleHandler(hours, index);
        setHours(data.sort((nextHour, currentHour) => nextHour - currentHour));
        onClickHandler({ hour: '', hours: data });
    };

    const addDays = (index) => {
        const data = dataListToggleHandler(days, index);
        setDays(data.sort((nextDay, currentDay) => nextDay - currentDay));
        onClickHandler({ day: '', days: data });
    };

    const addWeekDays = (index) => {
        const data = dataListToggleHandler(weekDays, index);
        setWeekDays(data);
        onClickHandler({ weeks: data });
    };

    const addMonths = (index) => {
        const data = dataListToggleHandler(months, index);
        setMonths(data);
        onClickHandler({ month: '', months: data });
    };

    const addYears = (value) => {
        const data = dataListToggleHandler(years, value);
        setYears(data);
        onClickHandler({ year: '', years: data });
    };

    const dropdownOnChange = (e) => {
        setDictData({
            days: days,
            weeks: weekDays,
            months: months,
            years: years,
            day: day,
            month: month,
            year: year,
            viewType: key,
        });
        handleViewChange(e.value);
        init(e.value);
    };

    const getYears = () => {
        const years: string[] = [];
        const date = customMoment();
        let currentYear = +date.get('year');

        for (let i = 0; i <= 11; i++) {
            years.push(date.set('year', currentYear++).format('YYYY'));
        }

        return years;
    };

    const init = (viewType) => {
        // let viewTypeKey = isNil(viewType) ? key : viewType;
        const data = dictData[viewType];

        if (isNil(data)) {
            setDictData(
                {
                    hours: [],
                    days: [],
                    weeks: [],
                    months: [],
                    years: [],
                    hour: '',
                    day: '',
                    month: '',
                    year: '',
                    viewType,
                    isActivePicker: false,
                },
                viewType,
            );
        }
    };

    useEffect(() => {
        init(key);
    }, []);

    useEffect(() => {
        const data = dictData[key];

        if (!isNil(data)) {
            setHours(data.hours);
            setDays(data.days);
            setWeekDays(data.weeks);
            setMonths(data.months);
            setYears(data.years);
            setHour(data.hour);
            setDay(data.day);
            setMonth(data.month);
            setYear(data.year);
        }
        /*eslint react-hooks/exhaustive-deps:0*/
    }, [dictData, modalView]);

    useEffect(() => {
        updateSchedulerInfo(dictData[key]);
    }, [modalView]);

    const getRecCount = (cron, startDate, endDate, timeZone) => {
        if (!isNil(cron)) {
            doPostRequest(getScheduleRunDates.request, {
                requestBody: { cronExpression: cron, startDate: startDate, endDate: endDate, timeZone: timeZone },
                successCallback: (Data) => {
                    setRecurrenceDataLocal(Data);
                },
            });
        }
    };

    const getWidthForInfoBlock = () => {
        if (isVisibleYear) return 'lg';
        if (isVisibleMonth) return 'md';

        return 'sm';
    };

    return (
        <div className="scheduler-modal-wrap-inner-annually">
            {!hideDropdown && (
                <>
                    <div className="title-text">{t(l.CreateCustomRecurrence)}</div>
                    <div className="title-next-text">{t(l.CreateAMoreFlexibleScheduleForYourReport)}</div>
                </>
            )}
            {isModalView && (
                <div className={`scheduler-modal-info-wrapper  width-${getWidthForInfoBlock()}`}>
                    <p className="scheduler-modal-info-message">{recurrenceDataLocal?.Description}</p>
                    <div className="scheduler-modal-run-info-wrapper">
                        <div className="scheduler-modal-run-info">
                            <p>{t(l.RecurrenceCount)}</p>
                            <Popover
                                className="next-run-date-popover-container"
                                extendTargetWidth={false}
                                Content={<RecurrenceList upcomingExecutions={recurrenceData?.UpcomingExecutions} />}
                                align="center"
                            >
                                <button disabled={recurrenceDataLocal?.Count === 0}>
                                    {recurrenceDataLocal?.Count}
                                </button>
                            </Popover>
                        </div>
                        <div className="scheduler-modal-run-info">
                            <p>{t(l.NextRunDate)}</p>
                            {recurrenceDataLocal?.UpcomingExecutions?.length > 0 && (
                                <span>
                                    {customMomentWithoutTimezoneConversion(
                                        first<AnyObject>(recurrenceDataLocal?.UpcomingExecutions)?.SelectedTimeZoneTime,
                                    ).format(partnerDateTimeWithoutSecondsFormat)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {!hideDropdown && (
                <Dropdown
                    defaultValue={options.find((item) => item.value === key)}
                    isMultiSelect={false}
                    disabled={false}
                    hasSearch={true}
                    inputSize="default"
                    placeholder={options.find((item) => item.value === key)?.label}
                    appearance="outline"
                    data={options}
                    searchPlaceholderText={t(l.Search)}
                    noDataText={t(l.NoDataFound)}
                    onChange={dropdownOnChange}
                />
            )}
            <div className="scheduler-days-wrap">
                {isVisibleYear && (
                    <div className="scheduler-days-inner">
                        <div className="every-days-block">
                            <span className="every-text">{t(l.Every)}</span>
                            <ExtendedInput
                                type="number"
                                min={1}
                                value={year}
                                onChange={addYear}
                                onKeyDown={handleIntegerKeyDown}
                                onPaste={handleIntegerPaste}
                                onDrop={handleIntegerDrop}
                            />
                            <span className="every-text lowercase-text">{t(l.Years)}</span>
                        </div>
                        <div className="or-text">{t(l.Or)} </div>
                        <div className="choose-text">{t(l.ChooseCustomYear)}</div>
                        <div className={classNames({ active: true }, 'years-wrap')}>
                            <div className="years-wrap-inner">
                                <DateSelector options={getYears()} onClick={addYears} data={years}></DateSelector>
                            </div>
                        </div>
                    </div>
                )}
                {isVisibleMonth && (
                    <div className="scheduler-days-inner">
                        <div className="every-days-block">
                            <span className="every-text">{t(l.Every)}</span>
                            <ExtendedInput
                                type="number"
                                min={1}
                                max={12}
                                value={month}
                                onChange={addMonth}
                                onKeyDown={handleIntegerKeyDown}
                                onPaste={handleIntegerPaste}
                                onDrop={handleIntegerDrop}
                            />
                            <span className="every-text lowercase-text">{t(l.Months)}</span>
                        </div>
                        <div className="or-text">{t(l.Or)} </div>
                        <div className="choose-text">{t(l.ChooseCustomMonths)}</div>
                        <div className={classNames({ active: true }, 'months-wrap')}>
                            <DateSelector
                                options={Months.map((month) => l[month])}
                                onClick={addMonths}
                                data={months}
                            ></DateSelector>
                        </div>
                    </div>
                )}
                {isVisibleWeek && (
                    <div className="weekly-wrap">
                        <div className="choose-text">{t(l.ChooseCustomWeekdays)}</div>
                        <div className="weeks">
                            <DateSelector
                                options={WeekDaysKeyList.map((weekDay) => l[weekDay])}
                                onClick={addWeekDays}
                                data={weekDays}
                            ></DateSelector>
                        </div>
                    </div>
                )}
                {isVisibleDay && (
                    <div className="scheduler-days-inner">
                        <div className="every-days-block">
                            <span className="every-text">{t(l.Every)}</span>
                            <ExtendedInput
                                type="number"
                                min={1}
                                max={31}
                                value={day}
                                onChange={addDay}
                                onKeyDown={handleIntegerKeyDown}
                                onPaste={handleIntegerPaste}
                                onDrop={handleIntegerDrop}
                            />
                            <span className="every-text">{t(l.Days)}</span>
                        </div>
                        <div className="or-text">{t(l.Or)} </div>
                        <div className="choose-text">{t(l.ChooseCustomDays)}</div>
                        <div className={classNames({ active: true }, 'days-wrap')}>
                            <DateSelector options={Days} onClick={addDays} data={days}></DateSelector>
                        </div>
                    </div>
                )}
                {isVisibleHour && (
                    <div className="scheduler-days-inner">
                        <div className="every-days-block">
                            <span className="every-text">{t(l.Every)}</span>
                            <ExtendedInput
                                type="number"
                                min={1}
                                max={23}
                                value={hour}
                                onChange={addHour}
                                onKeyDown={handleIntegerKeyDown}
                                onPaste={handleIntegerPaste}
                                onDrop={handleIntegerDrop}
                            />
                            <span className="every-text">{t(l.Hours)}</span>
                        </div>
                        <div className="or-text">{t(l.Or)} </div>
                        <div className="choose-text">{t(l.ChooseCustomHours)}</div>
                        <div className={classNames({ active: true }, 'days-wrap')}>
                            <DateSelector options={Hours} onClick={addHours} data={hours}></DateSelector>
                        </div>
                    </div>
                )}
            </div>
            {schedulerValidationErrorText && <p className="error-text">{t(schedulerValidationErrorText)}</p>}
        </div>
    );
};

ScheduleCEView.propTypes = {
    modalView: PropTypes.shape({
        key: PropTypes.string.isRequired,
        isVisibleSave: PropTypes.bool,
        isVisibleDay: PropTypes.bool,
        isVisibleWeek: PropTypes.bool,
        isVisibleMonth: PropTypes.bool,
        isVisibleYear: PropTypes.bool,
        isVisibleHour: PropTypes.bool,
    }).isRequired,
    dictData: PropTypes.object,
    handleViewChange: PropTypes.func,
    setDictData: PropTypes.func,
    hideDropdown: PropTypes.bool.isRequired,
    recurrenceData: PropTypes.object,
    schedulerValidationErrorText: PropTypes.string,
    timeOptions: PropTypes.object,
    isModalView: PropTypes.bool,
};

ScheduleCEView.defaultProps = {
    modalView: {
        isVisibleSave: false,
        isVisibleDay: false,
        isVisibleWeek: false,
        isVisibleMonth: false,
        isVisibleYear: false,
    },
};

export default memo(ScheduleCEView);
